import React, { useRef } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import CustomDateRangePicker from '../CustomDateRangePicker/CustomDateRangePicker';
import useDebouncedEffect from '../../../hooks/useDebouncedEffect';

interface ConversationsDateRangeContainerProps {
  dateRange: [Dayjs, Dayjs];
  setDateRange: (range: [Dayjs, Dayjs]) => void;
  handleSetFilterConfig: (
    newStartOfRange: dayjs.Dayjs | null,
    newEndOfRange: dayjs.Dayjs | null,
    clearSearch?: boolean
  ) => void;
  daysInRange: number;
}

function ConversationsDateRangeContainer({
  dateRange,
  setDateRange,
  handleSetFilterConfig,
  daysInRange
}: ConversationsDateRangeContainerProps): React.ReactElement {
  const isFirstRenderRef = useRef(true);
  const userInitiatedChangeRef = useRef(false);
  const prevDateRangeRef = useRef<[Dayjs, Dayjs]>(dateRange);

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const handleChangeDateRange = (
    startOfRange: dayjs.Dayjs,
    endOfRange: dayjs.Dayjs
  ): void => {
    userInitiatedChangeRef.current = true;
    prevDateRangeRef.current = [startOfRange, endOfRange];
    setDateRange([startOfRange, endOfRange]);
    handleSetFilterConfig(startOfRange, endOfRange);
  };

  const handleSubmit = (ev: React.FormEvent): void => {
    ev.preventDefault();
  };

  useDebouncedEffect(
    () => {
      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false;
        prevDateRangeRef.current = dateRange;
        return;
      }

      if (userInitiatedChangeRef.current) {
        userInitiatedChangeRef.current = false;
        return;
      }

      const [prevStart, prevEnd] = prevDateRangeRef.current;
      const [newStart, newEnd] = dateRange;
      const hasActualChange =
        !prevStart.isSame(newStart, 'minute') ||
        !prevEnd.isSame(newEnd, 'minute');

      prevDateRangeRef.current = dateRange;

      if (hasActualChange) {
        handleSetFilterConfig(null, null);
      }
    },
    [dateRange],
    500
  );

  return (
    <Box
      component='form'
      onSubmit={handleSubmit}
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: isTablet ? 'center' : 'flex-end',
        justifyContent: isTablet ? 'center' : 'flex-end',
        flexDirection: 'row',
        '@media (max-width: 1200px)': {
          justifyContent: 'center',
          flexDirection: 'column'
        }
      }}
    >
      {dateRange[0] && (
        <CustomDateRangePicker
          dateRange={dateRange}
          handleChangeDateRange={handleChangeDateRange}
          daysInRange={daysInRange}
        />
      )}
    </Box>
  );
}

export default ConversationsDateRangeContainer;
